import { roundMoney } from './currencyUtil';
import { userFacingError } from './imageCompressionUtil';
import {
  POS_CUSTOMIZATIONS_KEY,
  POS_CUSTOMIZATION_TYPES,
  POS_CUSTOMIZATIONS_MAX,
  POS_CUSTOMIZATION_CHOICES_MAX,
  POS_CUSTOMIZATION_LABEL_MAX_LENGTH,
  POS_CUSTOMIZATION_TEXT_MAX_LENGTH,
  POS_CUSTOMIZATION_PRICE_MAX,
  POS_EXTRAS_KEY,
  POS_EXTRAS_MAX,
  POS_EXTRA_DESCRIPTION_MAX_LENGTH,
  POS_EXTRA_QTY_MAX,
  POS_EXTRA_SELECTION_LABEL,
} from '../constants/posConst';

// Product customization options — the sugar level, add-ons and "name on cup"
// questions a product asks when it is sold. Everything that shapes, checks or
// prices them lives here, so the product form, the till and the receipt all
// read an option the same way.
//
// Shapes used throughout:
//   option   { id, label, type: 'choice'|'text', required, multiple, choices: [{ name, price }] }
//   extra    { id, label, description, price } — a paid add-on counted per item
//   picks    { [optionId]: string[] (choice names) | string (typed answer),
//              [extraId]: number (how many) } — what the till holds while choosing
//   selected [{ label, value, price }] — the answered options, as carried on a cart line and stored on an order

/**
 * A short random id for a new option. Only has to be unique within one
 * product, so a timestamp plus a few random characters is plenty.
 *
 * @returns {string} e.g. "opt_lq3k9x_a7f2"
 */
export const createPosCustomizationId = () => (
  `opt_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`
);

/**
 * Trims and caps a label or choice name.
 *
 * @param {*} value
 * @returns {string}
 */
const cleanLabel = (value) => String(value ?? '').trim().slice(0, POS_CUSTOMIZATION_LABEL_MAX_LENGTH);

/**
 * A choice's extra price as a number, or NaN when it is not a usable amount.
 * Blank means "no extra charge", which is the common case (sugar levels).
 *
 * @param {number|string} value
 * @returns {number}
 */
const parseChoicePrice = (value) => {
  if (value === undefined || value === null || String(value).trim() === '') return 0;
  const price = Number(value);
  if (!Number.isFinite(price) || price < 0 || price > POS_CUSTOMIZATION_PRICE_MAX) return NaN;
  return roundMoney(price);
};

/**
 * Firebase hands back an array whose middle was ever deleted as an OBJECT
 * keyed by index. Both shapes are read as a plain list so nothing downstream
 * has to care which one arrived.
 *
 * @param {*} value
 * @returns {Array}
 */
const asList = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (value && typeof value === 'object') return Object.values(value).filter(Boolean);
  return [];
};

/**
 * The customization options a product asks, safe to render.
 *
 * Tolerant rather than strict: a record that is half-written or from an older
 * shape is skipped instead of breaking the till, because a product that
 * cannot be sold is a worse outcome than one missing a question.
 *
 * @param {object} product - a POS product
 * @returns {Array<{id: string, label: string, type: string, required: boolean, multiple: boolean, choices: Array<{name: string, price: number}>}>}
 */
export const posProductCustomizations = (product) => asList(product?.[POS_CUSTOMIZATIONS_KEY])
  .map((option) => {
    const type = option.type === POS_CUSTOMIZATION_TYPES.TEXT ? POS_CUSTOMIZATION_TYPES.TEXT : POS_CUSTOMIZATION_TYPES.CHOICE;
    const choices = type === POS_CUSTOMIZATION_TYPES.CHOICE
      ? asList(option.choices)
        .map((choice) => ({ name: cleanLabel(choice?.name), price: parseChoicePrice(choice?.price) || 0 }))
        .filter((choice) => choice.name)
      : [];
    return {
      id: String(option.id || ''),
      label: cleanLabel(option.label),
      type,
      required: Boolean(option.required),
      multiple: type === POS_CUSTOMIZATION_TYPES.CHOICE && Boolean(option.multiple),
      choices,
    };
  })
  // A selection with nothing to select is not a question the till can ask.
  .filter((option) => option.id && option.label
    && (option.type === POS_CUSTOMIZATION_TYPES.TEXT || option.choices.length > 0));

/**
 * A short random id for a new extra. Prefixed differently from options so the
 * two can share the till's `picks` object without ever colliding.
 *
 * @returns {string} e.g. "ext_lq3k9x_a7f2"
 */
export const createPosExtraId = () => (
  `ext_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`
);

/**
 * The extras a product offers, safe to render. Tolerant in the same way as
 * posProductCustomizations: a half-written record is skipped, not fatal.
 *
 * @param {object} product - a POS product
 * @returns {Array<{id: string, label: string, description: string, price: number}>}
 */
export const posProductExtras = (product) => asList(product?.[POS_EXTRAS_KEY])
  .map((extra) => ({
    id: String(extra.id || ''),
    label: cleanLabel(extra.label),
    description: String(extra.description ?? '').trim().slice(0, POS_EXTRA_DESCRIPTION_MAX_LENGTH),
    price: parseChoicePrice(extra.price) || 0,
  }))
  .filter((extra) => extra.id && extra.label);

/**
 * Whether a product asks anything when it is sold — an option to answer or an
 * extra to count.
 *
 * @param {object} product
 * @returns {boolean}
 */
export const posHasCustomizations = (product) => (
  posProductCustomizations(product).length > 0 || posProductExtras(product).length > 0
);

/**
 * Checks and cleans one option as entered in the option form.
 *
 * Throws with a message written for the person filling the form in, so the
 * modal can show it as-is.
 *
 * @param {{id?: string, label: string, type: string, required?: boolean, multiple?: boolean, choices?: Array<{name: string, price: number|string}>}} draft
 * @param {Array<{id: string, label: string}>} [siblings] - the product's other
 *   options, so two cannot share a label (the receipt would read ambiguously)
 * @returns {{id: string, label: string, type: string, required: boolean, multiple: boolean, choices: Array<{name: string, price: number}>}}
 * @throws {Error} user-facing, on anything the till could not ask sensibly
 */
export const normalisePosCustomization = (draft, siblings = []) => {
  const label = cleanLabel(draft?.label);
  if (!label) throw userFacingError('Give the option a label, e.g. "Sugar level".');

  const id = String(draft?.id || '') || createPosCustomizationId();
  const clash = siblings.some((other) => other.id !== id && cleanLabel(other.label).toLowerCase() === label.toLowerCase());
  if (clash) throw userFacingError(`This product already has an option called "${label}".`);

  const type = draft?.type === POS_CUSTOMIZATION_TYPES.TEXT ? POS_CUSTOMIZATION_TYPES.TEXT : POS_CUSTOMIZATION_TYPES.CHOICE;
  const required = Boolean(draft?.required);

  if (type === POS_CUSTOMIZATION_TYPES.TEXT) {
    return { id, label, type, required, multiple: false, choices: [] };
  }

  // Blank rows are dropped rather than refused: the form always offers one
  // empty row to type into, and leaving it empty is not a mistake.
  const rows = asList(draft?.choices).filter((choice) => String(choice?.name ?? '').trim() !== '');
  if (rows.length === 0) throw userFacingError('Add at least one choice to pick from.');
  if (rows.length > POS_CUSTOMIZATION_CHOICES_MAX) {
    throw userFacingError(`An option can list up to ${POS_CUSTOMIZATION_CHOICES_MAX} choices.`);
  }

  const seen = new Set();
  const choices = rows.map((choice) => {
    const name = cleanLabel(choice.name);
    const key = name.toLowerCase();
    if (seen.has(key)) throw userFacingError(`"${name}" is listed twice.`);
    seen.add(key);
    const price = parseChoicePrice(choice.price);
    if (Number.isNaN(price)) {
      throw userFacingError(`Enter an extra price for "${name}" between 0 and ${POS_CUSTOMIZATION_PRICE_MAX}, or leave it blank.`);
    }
    return { name, price };
  });

  return { id, label, type, required, multiple: Boolean(draft?.multiple), choices };
};

/**
 * Checks a product's whole option list before it is saved.
 *
 * @param {Array<object>} list - options as held by the product form
 * @returns {Array<object>} the cleaned list, in the same order
 * @throws {Error} user-facing
 */
export const normalisePosCustomizations = (list) => {
  const options = asList(list);
  if (options.length > POS_CUSTOMIZATIONS_MAX) {
    throw userFacingError(`A product can have up to ${POS_CUSTOMIZATIONS_MAX} customization options.`);
  }
  return options.map((option) => normalisePosCustomization(option, options));
};

/**
 * Checks and cleans one extra as entered in the extra form.
 *
 * @param {{id?: string, label: string, description?: string, price: number|string}} draft
 * @param {Array<{id: string, label: string}>} [siblings] - the product's other
 *   extras, so two cannot share a name
 * @returns {{id: string, label: string, description: string, price: number}}
 * @throws {Error} user-facing
 */
export const normalisePosExtra = (draft, siblings = []) => {
  const label = cleanLabel(draft?.label);
  if (!label) throw userFacingError('Give the extra a label, e.g. "Pearl".');

  const id = String(draft?.id || '') || createPosExtraId();
  const clash = siblings.some((other) => other.id !== id && cleanLabel(other.label).toLowerCase() === label.toLowerCase());
  if (clash) throw userFacingError(`This product already has an extra called "${label}".`);

  // Unlike a choice, an extra's price is asked for outright — an add-on is
  // usually charged — so blank is refused rather than read as free. 0 is
  // still allowed, for an extra given away on purpose.
  if (draft?.price === undefined || draft?.price === null || String(draft.price).trim() === '') {
    throw userFacingError('Enter the add-on price (0 if it is free).');
  }
  const price = parseChoicePrice(draft.price);
  if (Number.isNaN(price)) {
    throw userFacingError(`Enter an add-on price between 0 and ${POS_CUSTOMIZATION_PRICE_MAX}.`);
  }

  const description = String(draft?.description ?? '').trim();
  if (description.length > POS_EXTRA_DESCRIPTION_MAX_LENGTH) {
    throw userFacingError(`Keep the description under ${POS_EXTRA_DESCRIPTION_MAX_LENGTH} characters.`);
  }

  return { id, label, description, price };
};

/**
 * Checks a product's whole extras list before it is saved.
 *
 * @param {Array<object>} list - extras as held by the product form
 * @returns {Array<object>} the cleaned list, in the same order
 * @throws {Error} user-facing
 */
export const normalisePosExtras = (list) => {
  const extras = asList(list);
  if (extras.length > POS_EXTRAS_MAX) {
    throw userFacingError(`A product can have up to ${POS_EXTRAS_MAX} extras.`);
  }
  return extras.map((extra) => normalisePosExtra(extra, extras));
};

/**
 * How many of one extra the till may record for one item: a whole number
 * from 0 to POS_EXTRA_QTY_MAX. Anything else reads as 0.
 *
 * @param {*} value
 * @returns {number}
 */
export const posExtraQuantity = (value) => {
  const qty = Math.floor(Number(value));
  if (!Number.isFinite(qty) || qty < 0) return 0;
  return Math.min(qty, POS_EXTRA_QTY_MAX);
};

/**
 * One line describing an option, for the list in the product form.
 *
 * @param {object} option - a normalised option
 * @returns {string} e.g. "Selection · 3 choices · Required · Can pick more than one"
 */
export const describePosCustomization = (option) => {
  const parts = [];
  if (option?.type === POS_CUSTOMIZATION_TYPES.TEXT) {
    parts.push('Custom input');
  } else {
    const count = asList(option?.choices).length;
    parts.push('Selection', `${count} choice${count === 1 ? '' : 's'}`);
    if (option?.multiple) parts.push('Can pick more than one');
  }
  parts.push(option?.required ? 'Required' : 'Optional');
  return parts.join(' · ');
};

/**
 * Turns what was picked at the till into the answered options for a cart line,
 * priced against the product AS IT IS NOW.
 *
 * Priced from the live product rather than from a figure captured when the
 * line was added, for the same reason the base price is: an add-on repriced
 * in Inventory mid-order must not leave the rail quoting the old amount.
 * A pick whose choice has since been removed is dropped.
 *
 * @param {object} product - a POS product
 * @param {Object<string, string[]|string|number>} picks - see the shapes at the top
 * @returns {{selected: Array<{label: string, value: string, price: number, name?: string, quantity?: number, unitPrice?: number}>, extra: number, missing: string[]}}
 *   `selected` lists the answered options, then the chosen extras; `extra` is
 *   what they add to ONE item; `missing` names the required options still
 *   unanswered, in the order the product asks them.
 */
export const resolvePosSelections = (product, picks = {}) => {
  const selected = [];
  const missing = [];
  let extra = 0;

  posProductCustomizations(product).forEach((option) => {
    const pick = picks?.[option.id];

    if (option.type === POS_CUSTOMIZATION_TYPES.TEXT) {
      const value = String(pick ?? '').trim().slice(0, POS_CUSTOMIZATION_TEXT_MAX_LENGTH);
      if (value) selected.push({ label: option.label, value, price: 0 });
      else if (option.required) missing.push(option.label);
      return;
    }

    const wanted = new Set(asList(Array.isArray(pick) ? pick : [pick]).map(String));
    // In the PRODUCT's order, not the order they were tapped, so the same
    // combination always reads — and keys — the same way.
    const chosen = option.choices.filter((choice) => wanted.has(choice.name));
    const kept = option.multiple ? chosen : chosen.slice(0, 1);
    if (kept.length === 0) {
      if (option.required) missing.push(option.label);
      return;
    }
    const price = roundMoney(kept.reduce((sum, choice) => sum + choice.price, 0));
    extra += price;
    selected.push({ label: option.label, value: kept.map((choice) => choice.name).join(', '), price });
  });

  // EXTRAS come after the options, one entry each, in the product's order.
  // Folded into the same list so everything that already reads `selected` —
  // the cart key, the rail, the receipt, Sales, the audit trail — shows them
  // without knowing extras exist: "Extra: Pearl × 2".
  posProductExtras(product).forEach((item) => {
    const qty = posExtraQuantity(picks?.[item.id]);
    if (qty <= 0) return;
    const price = roundMoney(item.price * qty);
    extra += price;
    selected.push({
      label: POS_EXTRA_SELECTION_LABEL,
      value: qty > 1 ? `${item.label} × ${qty}` : item.label,
      price,
      // Kept apart as well, so the receipt can give the extra its own
      // Qty / Unit Price / Amount row instead of one lump "× 2" figure.
      name: item.label,
      quantity: qty,
      unitPrice: item.price,
    });
  });

  return { selected, extra: roundMoney(extra), missing };
};

/**
 * The key a cart line is stored under.
 *
 * The same product with DIFFERENT answers is a different line — a 50% sugar
 * and a 100% sugar milk tea are two things to make — while the same answers
 * twice is one line with a bigger quantity.
 *
 * @param {string} productId
 * @param {Array<{label: string, value: string}>} selected - from resolvePosSelections
 * @returns {string}
 */
export const posCartLineKey = (productId, selected = []) => (
  selected.length === 0
    ? String(productId)
    : `${productId}::${selected.map((option) => `${option.label}=${option.value}`).join('|')}`
);

/**
 * The answered options as one readable line.
 *
 * @param {Array<{label: string, value: string}>} selected
 * @returns {string} e.g. "Sugar level: 50% · Add-ons: Pearl, Coffee jelly"; '' when none
 */
export const describePosSelections = (selected) => asList(selected)
  .map((option) => `${option.label}: ${option.value}`)
  .join(' · ');

/**
 * An order line's name with its options, for places that show a line as one
 * string — the Sales list, the Excel export and the audit trail.
 *
 * @param {{name: string, options?: Array<{label: string, value: string}>}} item
 * @returns {string} e.g. "Milktea (Sugar level: 50% · Add-ons: Pearl)"
 */
export const posOrderItemLabel = (item) => {
  const options = describePosSelections(item?.options);
  return options ? `${item?.name || ''} (${options})` : (item?.name || '');
};
